/**
 * Utility functions for handling image URLs and Cloudinary integration
 */

// Format Cloudinary URL based on cloud name, folder and image details
export const formatCloudinaryUrl = (cloudName, folderName, publicId, format = 'jpg') => {
  if (!cloudName || !publicId) return '';
  
  const baseUrl = `https://res.cloudinary.com/${cloudName}/image/upload`;
  const folderPath = folderName ? `${folderName}/` : '';
  return `${baseUrl}/v1/${folderPath}${publicId}.${format}`;
};

// Validate if a URL is a valid image URL
export const isValidImageUrl = (url) => {
  if (!url) return false;
  
  // Basic URL validation
  try {
    new URL(url);
  } catch (e) {
    return false;
  }
  
  // Check if URL ends with common image extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
  const lowercaseUrl = url.toLowerCase();
  
  // Either ends with an image extension or comes from common image hosting services
  return (
    imageExtensions.some(ext => lowercaseUrl.endsWith(ext)) ||
    lowercaseUrl.includes('cloudinary.com') ||
    lowercaseUrl.includes('imgur.com') ||
    lowercaseUrl.includes('unsplash.com') ||
    lowercaseUrl.includes('googleusercontent.com/img')
  );
};

// Parse Cloudinary URL to extract cloud name and folder
export const parseCloudinaryUrl = (url) => {
  if (!url || !url.includes('cloudinary.com')) {
    return { cloudName: '', folderName: '', publicId: '' };
  }
  
  try {
    // Sample URL: https://res.cloudinary.com/cloud-name/image/upload/v1/folder-name/image-id.jpg
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    
    // Extract cloud name from hostname
    const cloudName = urlObj.hostname.split('.')[0];
    
    // Find position of v1 or v2 in the path
    const versionIndex = pathParts.findIndex(part => part.startsWith('v'));
    
    // Extract folder name and public ID if possible
    let folderName = '';
    let publicId = '';
    
    if (versionIndex !== -1 && pathParts.length > versionIndex + 1) {
      // If there's at least one path segment after the version
      if (pathParts.length === versionIndex + 2) {
        // No folder, just image ID
        publicId = pathParts[versionIndex + 1].split('.')[0];
      } else {
        // Has folder
        folderName = pathParts[versionIndex + 1];
        // The rest is the image ID
        publicId = pathParts[versionIndex + 2].split('.')[0];
      }
    }
    
    return { cloudName, folderName, publicId };
  } catch (e) {
    console.error("Error parsing Cloudinary URL:", e);
    return { cloudName: '', folderName: '', publicId: '' };
  }
};

// Simulate Cloudinary upload (in a real app, this would be an actual API call)
export const simulateCloudinaryUpload = async (cloudName, folderName, uploadPreset, files) => {
  if (!cloudName || !uploadPreset || !files.length) {
    throw new Error("Cloud name, upload preset, and files are required");
  }
  
  // Convert FileList to Array for easier processing
  const fileArray = Array.from(files);
  
  // Validate file types
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const invalidFiles = fileArray.filter(file => !validTypes.includes(file.type));
  
  if (invalidFiles.length > 0) {
    throw new Error(`Invalid file types: ${invalidFiles.map(f => f.name).join(', ')}`);
  }
  
  // In a real implementation, you would use Cloudinary's Upload API or SDK here
  // For simulation, we'll create mock URLs based on the file names
  
  // Simulate upload delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Generate simulated URLs for each file
  const uploadedUrls = fileArray.map(file => {
    // Create a "public ID" from the filename (removing extension)
    const fileName = file.name.replace(/\.[^/.]+$/, "");
    // Determine format from file type
    const format = file.type.split('/')[1];
    // Create a Cloudinary-like URL with folder structure if provided
    const folderPath = folderName ? `${folderName}/` : '';
    return `https://res.cloudinary.com/${cloudName}/image/upload/v1/${folderPath}${fileName}.${format}`;
  });
  
  return {
    success: true,
    urls: uploadedUrls,
    uploadedCount: fileArray.length
  };
};

// Helper function to sanitize folder names for Cloudinary
export const sanitizeFolderName = (name) => {
  if (!name) return '';
  
  // Replace spaces with underscores and remove special characters
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '')
    .trim();
};

// Upload image to Cloudinary using unsigned upload preset
export const uploadToCloudinary = async (file, cloudName, uploadPreset) => {
    if (!file || !cloudName || !uploadPreset) {
        throw new Error("File, Cloud Name, and Upload Preset are required");
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", uploadPreset); // Required for unsigned uploads

    try {
        const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
            method: "POST",
            body: formData
        });

        const data = await response.json();
        if (data.secure_url) {
            return { success: true, url: data.secure_url };
        } else {
            throw new Error("Upload failed: " + (data.error?.message || "Unknown error"));
        }
    } catch (error) {
        console.error("Upload error:", error);
        return { success: false, error: error.message };
    }
};

// Upload multiple files to Cloudinary
export const uploadMultipleToCloudinary = async (files, cloudName, uploadPreset, folderName = null, useSequentialNaming = false, existingUrls = []) => {
    if (!files || !files.length || !cloudName || !uploadPreset) {
        throw new Error("Files, Cloud Name, and Upload Preset are required");
    }

    const results = {
        success: true,
        urls: [],
        errors: []
    };

    // Find the highest image number if using sequential naming
    let startingNumber = 1;
    if (useSequentialNaming && existingUrls && existingUrls.length > 0) {
        // Extract numbers from existing image URLs that match the pattern "image{number}"
        const numberPattern = /image(\d+)/;
        const numbers = existingUrls
            .filter(url => url && url.includes(`/${folderName}/`)) // Only consider URLs in the same folder
            .map(url => {
                const match = url.match(numberPattern);
                return match ? parseInt(match[1], 10) : 0;
            })
            .filter(num => !isNaN(num));
        
        // Find the maximum number and add 1 for the next sequence
        if (numbers.length > 0) {
            startingNumber = Math.max(...numbers) + 1;
        }
    }

    // Use Promise.all to upload files in parallel
    const uploadPromises = Array.from(files).map(async (file, index) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("upload_preset", uploadPreset);
            
            // If folder name is provided, include it
            if (folderName) {
                formData.append("folder", folderName);
            }
            
            // Add public_id for sequential naming if enabled, using the new starting number
            if (useSequentialNaming) {
                const imageNumber = startingNumber + index;
                formData.append("public_id", `image${imageNumber}`);
            }

            const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
                method: "POST",
                body: formData
            });

            const data = await response.json();
            if (data.secure_url) {
                results.urls.push(data.secure_url);
                return { success: true, url: data.secure_url };
            } else {
                const errorMsg = "Upload failed: " + (data.error?.message || "Unknown error");
                results.errors.push({ file: file.name, error: errorMsg });
                return { success: false, error: errorMsg };
            }
        } catch (error) {
            results.errors.push({ file: file.name, error: error.message });
            return { success: false, error: error.message };
        }
    });

    await Promise.all(uploadPromises);
    
    // Overall success is true only if all uploads succeeded
    results.success = results.errors.length === 0;
    results.uploadedCount = results.urls.length;
    
    return results;
};
